import { useTranslate } from "@tolgee/react";
import { AlertTriangle, Info, Loader2 } from "lucide-react";
import { useState } from "react";
import { getHyperliquidErrorMessage } from "#/features/trade/lib/hyperliquid-error";
import { formatUsd, toNumber } from "#/lib/formatters";
import { cn } from "#/lib/utils";
import { sanitizeAmountInput, usePrefillMaxOnce } from "../shared-fund-flow";

type DepositConfirmResult = {
	hash?: string;
	transactionHash?: string;
};

export function StepDepositUSDC({
	walletBalance,
	usdtBalance,
	isPending,
	readOnly,
	needsBridgeToTrading,
	setIsPending,
	onConfirm,
	onSuccess,
}: {
	walletBalance: number;
	usdtBalance: number;
	isPending: boolean;
	readOnly: boolean;
	needsBridgeToTrading?: boolean;
	setIsPending: (v: boolean) => void;
	onConfirm?:
		| ((amount: number) => Promise<DepositConfirmResult | undefined>)
		| ((amount: number) => Promise<void>);
	onSuccess: (txHash: string | null, amount: string) => void;
}) {
	const { t } = useTranslate();
	const [amount, setAmount] = useState("");
	const [error, setError] = useState<string | null>(null);

	const maxAmount = walletBalance > 0 ? String(walletBalance) : "";
	usePrefillMaxOnce(!readOnly, maxAmount, amount, setAmount);

	function handleAmountChange(value: string) {
		setAmount(sanitizeAmountInput(value));
		setError(null);
	}

	function handleMax() {
		if (walletBalance <= 0 || readOnly) return;
		setAmount(maxAmount);
	}

	const enteredAmount = toNumber(amount);
	const exceedsBalance = enteredAmount > walletBalance;
	const belowMinimum = enteredAmount > 0 && enteredAmount < 5;
	const canConfirm =
		!readOnly &&
		enteredAmount >= 5 &&
		walletBalance > 0 &&
		enteredAmount <= walletBalance;

	function handleConfirm() {
		if (!canConfirm || isPending) return;
		setIsPending(true);
		setError(null);
		const confirmation =
			onConfirm?.(enteredAmount) ??
			Promise.resolve<DepositConfirmResult | undefined>(undefined);

		Promise.resolve(confirmation)
			.then((result) => {
				const r = result as DepositConfirmResult | undefined;
				onSuccess(r?.transactionHash ?? r?.hash ?? null, amount);
			})
			.catch((err: unknown) =>
				setError(getHyperliquidErrorMessage(t, err, t("deposit.failed"))),
			)
			.finally(() => setIsPending(false));
	}

	return (
		<div className="flex flex-col gap-3">
			<div className="overflow-hidden rounded-[14px] border border-trade-border-subtle bg-trade-surface-inset">
				<div className="flex items-center gap-3 px-4 py-3">
					<img
						src="https://sdk-cdn.fun.xyz/images/usdc.svg"
						width={24}
						height={24}
						alt="USDC"
					/>
					<div className="min-w-0 flex-1">
						<p className="text-body2-bold text-trade-text-strong">
							USDC
						</p>
						<p className="text-label2 text-trade-text-secondary">
							{t("form.available-funds")}
						</p>
					</div>
					<div className="text-right">
						<p className="text-label1-tabular-bold text-trade-text-strong">
							{walletBalance} USDC
						</p>
						<p className="text-label2 text-trade-text-secondary">
							{formatUsd(walletBalance)}
						</p>
					</div>
				</div>
			</div>

			{usdtBalance > 0 && (
				<div className="flex items-start gap-2 rounded-[12px] border border-[var(--color-theme-denotives-yellow)]/20 bg-[var(--color-theme-denotives-yellow)]/10 px-3.5 py-2.5 text-label2 text-[var(--color-theme-denotives-yellow)] dark:text-[var(--color-theme-denotives-yellow)]">
					<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
					<span>{t("deposit.usdt-warning")}</span>
				</div>
			)}

			<div className="rounded-[16px] border border-trade-border-subtle bg-trade-surface-inset px-3.5 py-3 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.02)]">
				<div className="flex items-center justify-between gap-3 text-caption1-medium uppercase text-trade-text-faint">
					<span>{t("form.amount")}</span>
					<button
						type="button"
						onClick={handleMax}
						disabled={isPending || readOnly}
						className="inline-flex min-h-6 items-center rounded-xl border border-primary/20 bg-primary/10 px-2 text-caption1-medium text-primary transition-colors hover:bg-primary/16 disabled:opacity-50 cursor-pointer"
					>
						{t("form.max")}
					</button>
				</div>

				<div className="mt-2 flex items-center gap-3">
					<input
						type="text"
						inputMode="decimal"
						value={amount}
						onChange={(e) => handleAmountChange(e.target.value)}
						placeholder="0.0"
						disabled={isPending || readOnly}
						aria-label="Deposit amount"
						className="h-10 w-full min-w-0 border-none bg-transparent px-0 text-title1 text-trade-text-strong outline-none placeholder:text-trade-text-faint disabled:opacity-50"
					/>
					<span className="shrink-0 text-label1-bold text-trade-text-faint">
						USDC
					</span>
				</div>

				<div
					className={cn(
						"mt-1 min-h-5 text-right text-caption1-medium",
						exceedsBalance || belowMinimum
							? "text-primary"
							: "text-transparent",
					)}
				>
					{exceedsBalance
						? t("validation.exceeds-balance", { balance: walletBalance })
						: belowMinimum
							? t("validation.minimum-deposit")
							: "."}
				</div>
			</div>

			{error && (
				<div className="flex items-start gap-2 rounded-[12px] border border-[var(--color-theme-status-negative)]/20 bg-[var(--color-theme-status-negative)]/10 px-3.5 py-2.5 text-label2 text-[var(--color-theme-status-negative)] dark:text-[var(--color-theme-status-negative)]">
					<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
					<span>{error}</span>
				</div>
			)}

			{needsBridgeToTrading && !readOnly && (
				<div className="flex items-start gap-2 rounded-[12px] border border-[var(--color-theme-denotives-skyblue)]/20 bg-[var(--color-theme-denotives-skyblue)]/10 px-3.5 py-2.5 text-label2 text-[var(--color-theme-denotives-skyblue)] dark:text-[var(--color-theme-denotives-skyblue)]">
					<Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
					<span>{t("deposit.step2.required-for-trading")}</span>
				</div>
			)}

			<button
				type="button"
				onClick={handleConfirm}
				disabled={!canConfirm || isPending}
				className="mt-1 flex min-h-12 w-full items-center justify-center gap-2 rounded-[14px] bg-primary text-body2-bold text-primary-foreground transition-[filter,transform,opacity] hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-45 cursor-pointer"
			>
				{isPending ? (
					<Loader2 className="h-4 w-4 animate-spin" />
				) : (
					t("actions.deposit")
				)}
			</button>
		</div>
	);
}
